// src/hooks/useTeam.js - Hook replacement that uses TeamStore
import { useMemo } from 'react';
import useTeamStore from '../stores/TeamStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing team data from TeamStore
 * 
 * UPDATED: Now uses Zustand TeamStore instead of TeamProvider
 * Store initialization is handled at app level in SocketContext.
 * 
 * FIXED: Uses proper Zustand selectors instead of destructuring entire store
 * 
 * @returns {object} Team data and operations
 */
export const useTeam = () => {
  const socket = useSocket();
  
  // 🚨 FIXED: Use individual selectors instead of destructuring entire store
  
  // Team data state
  const teamMembers = useTeamStore(state => state.teamMembers);
  const permissions = useTeamStore(state => state.permissions);
  const teamStats = useTeamStore(state => state.teamStats);
  const teamActivity = useTeamStore(state => state.teamActivity);
  const pendingInvitations = useTeamStore(state => state.pendingInvitations);
  
  // Granular loading states for different operations
  const teamLoading = useTeamStore(state => state.teamLoading);
  const permissionsLoading = useTeamStore(state => state.permissionsLoading);
  const roleUpdateLoading = useTeamStore(state => state.roleUpdateLoading);
  const activityLoading = useTeamStore(state => state.activityLoading);
  const inviteLoading = useTeamStore(state => state.inviteLoading);
  const invitationsLoading = useTeamStore(state => state.invitationsLoading);
  const savingMember = useTeamStore(state => state.savingMember);
  const teamError = useTeamStore(state => state.teamError);
  
  // Data loading operations (functions don't change, so we can get them once)
  const loadTeamMembers = useTeamStore(state => state.loadTeamMembers);
  const loadPermissions = useTeamStore(state => state.loadPermissions);
  const loadTeamActivity = useTeamStore(state => state.loadTeamActivity);
  const loadPendingInvitations = useTeamStore(state => state.loadPendingInvitations);
  
  // Team management operations
  const updateMemberRole = useTeamStore(state => state.updateMemberRole);
  const inviteTeamMember = useTeamStore(state => state.inviteTeamMember);
  const cancelInvitation = useTeamStore(state => state.cancelInvitation);
  const resendInvitation = useTeamStore(state => state.resendInvitation);
  const updateTeamMember = useTeamStore(state => state.updateTeamMember);
  const removeTeamMember = useTeamStore(state => state.removeTeamMember);
  const reactivateTeamMember = useTeamStore(state => state.reactivateTeamMember);
  
  // Utility functions
  const getMemberById = useTeamStore(state => state.getMemberById);
  const getMembersByRole = useTeamStore(state => state.getMembersByRole);
  const getOnlineMembers = useTeamStore(state => state.getOnlineMembers);
  const hasPermission = useTeamStore(state => state.hasPermission);
  const clearTeamError = useTeamStore(state => state.clearTeamError);
  const refreshTeamData = useTeamStore(state => state.refreshTeamData);
  const refreshPermissions = useTeamStore(state => state.refreshPermissions);
  const getInvitationById = useTeamStore(state => state.getInvitationById);
  const getInvitationsByEmail = useTeamStore(state => state.getInvitationsByEmail);
  const hasInvitationForEmail = useTeamStore(state => state.hasInvitationForEmail);
  const getCachedData = useTeamStore(state => state.getCachedData);
  const isDataFresh = useTeamStore(state => state.isDataFresh);
  
  // Computed value getters
  const getPermissionHelpers = useTeamStore(state => state.getPermissionHelpers);
  const getTeamStatsHelpers = useTeamStore(state => state.getTeamStatsHelpers);
  
  // Connection state
  const isConnected = useTeamStore(state => state.isConnected);
  
  // Internal initialization state (for fallback check)
  const _initialized = useTeamStore(state => state._initialized);
  
  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useTeam: Store not initialized at app level, trying fallback');
    socket.initializeStore('TeamStore');
  }
  
  // Computed values (from TeamProvider)
  const permissionHelpers = useMemo(() => {
    return getPermissionHelpers();
  }, [getPermissionHelpers]);
  
  const teamStatsHelpers = useMemo(() => {
    return getTeamStatsHelpers();
  }, [getTeamStatsHelpers]);
  
  // Return the exact same interface as the old TeamProvider
  return {
    // ================================
    // DATA STATE (updated from original hook)
    // ================================
    teamMembers,
    permissions,
    teamStats,
    teamActivity,
    pendingInvitations,
    
    // ================================
    // LOADING STATES (updated from original hook)
    // ================================
    loading: teamLoading,        // Keep original API name
    permissionsLoading,
    roleUpdateLoading,
    activityLoading,
    inviteLoading,
    invitationsLoading,
    savingMember,
    error: teamError,           // Keep original API name
    
    // ================================
    // PERMISSION HELPERS (updated from original hook)
    // ================================
    ...permissionHelpers,       // All permission flags (isAdmin, canManageTeam, etc.)
    
    // ================================
    // TEAM STATS HELPERS (updated from original hook)
    // ================================
    ...teamStatsHelpers,        // All team statistics
    
    // ================================
    // DATA LOADING OPERATIONS (now emit-only, no async)
    // ================================
    loadTeamMembers,
    loadPermissions,
    loadTeamActivity,
    loadPendingInvitations,
    
    // ================================
    // TEAM MANAGEMENT OPERATIONS (now emit-only, no async)
    // ================================
    updateMemberRole,
    updateTeamMember,
    inviteTeamMember,
    cancelInvitation,
    resendInvitation,
    removeTeamMember,
    reactivateTeamMember,
    
    // ================================
    // UTILITY FUNCTIONS (updated from original hook)
    // ================================
    getMemberById,
    getMembersByRole,
    getOnlineMembers,
    hasPermission,
    clearError: clearTeamError,
    refreshTeamData,
    refreshPermissions,
    
    // Invitation utility functions
    getInvitationById,
    getInvitationsByEmail,
    hasInvitationForEmail,
    
    // ================================
    // CONNECTION STATE (unchanged from original hook)
    // ================================
    isConnected: socket.isAuthenticated,
    
    // ================================
    // CACHE UTILITIES (unchanged from original hook)
    // ================================
    getCachedData,
    isDataFresh,
    
    // Legacy aliases for backward compatibility
    teamLoading: teamLoading,
    teamError: teamError,
    clearTeamError: clearTeamError
  };
};

export default useTeam;